import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import useFetchCsvMappingTemplates from '../api/useFetchCsvMappingTemplates';

export function CandidateImportRequestsPresetsTab() {
  const navigate = useNavigate();
  const { data: templates, isLoading, refetch } = useFetchCsvMappingTemplates();
  const [deletingTemplateId, setDeletingTemplateId] = useState<number | null>(null);

  // ローディング中の表示
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-x3l">
        <div className="text-body-md text-sub">読み込み中...</div>
      </div>
    );
  }

  const templateList = templates?.records || [];

  const handleEdit = (templateId: number) => {
    navigate({
      to: '/agent/candidates/candidate_import_requests/presets/$presetId',
      params: { presetId: String(templateId) },
    });
  };

  const handleDelete = async (templateId: number) => {
    if (!window.confirm('このプリセットを削除しますか？')) {
      return;
    }

    try {
      setDeletingTemplateId(templateId);
      const response = await fetch(`/agent/api/csv_mapping_templates/${templateId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token':
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
        },
      });

      if (!response.ok) {
        throw new Error('プリセットの削除に失敗しました');
      }

      await refetch();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'プリセットの削除に失敗しました');
    } finally {
      setDeletingTemplateId(null);
    }
  };

  return (
    <div className="space-y-md">
      {/* ヘッダー */}
      <h2 className="text-heading-md">取り込みプリセット</h2>
      {/* プリセット一覧 or 空状態 */}
      {templateList.length === 0 ? (
        <div className="rounded-sm bg-gray p-x3l text-center">
          <p className="text-body-md text-sub">取り込みプリセットがありません。</p>
        </div>
      ) : (
        <div className="space-y-0">
          {/* テーブルヘッダー */}
          <div className="flex items-center gap-x-lg border-b border-solid border-border bg-gray px-md py-xxs">
            <div className="flex-1 text-body-sm font-bold text-sub">タイトル</div>
            <div className="w-40 text-body-sm font-bold text-sub">作成者</div>
            <div className="w-32 text-right text-body-sm font-bold text-sub">作成日</div>
            <div className="w-44" />
          </div>

          {/* テーブル本体 */}
          {templateList.map((template) => (
            <div
              className="flex items-center gap-x-lg border-b border-solid border-border bg-white px-md py-sm"
              key={template.id}
            >
              <div className="flex-1">
                <p className="text-body-md text-main">{template.title}</p>
              </div>
              <div className="w-40">
                <p className="text-body-md text-main">{template.creator_name || '-'}</p>
              </div>
              <div className="w-32 text-right">
                <p className="text-body-md text-main">
                  {template.created_at
                    ? format(new Date(template.created_at), 'yyyy/MM/dd', { locale: ja })
                    : '-'}
                </p>
              </div>
              <div className="flex w-44 items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => handleEdit(template.id)}
                  className="rounded border border-solid border-border bg-white px-sm py-xxs text-button-sm text-main hover:bg-gray"
                >
                  編集
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(template.id)}
                  disabled={deletingTemplateId === template.id}
                  className="rounded border border-solid border-border bg-white px-sm py-xxs text-button-sm text-danger hover:bg-danger_lighten disabled:opacity-50"
                >
                  削除
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
